import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  useMutation,
} from "@tanstack/react-query";
import qs from "qs";
import {
  editSceduler,
  getDropDownLists,
  getProductionDaysList,
  getTemplatesDetails,
  getTemplatesList,
  saveProductionDays,
  updateName,
  updateStatus,
  copycreateTemplate,
  createTemplate,
  getRecipeLists,
  removeTemplate,
  updateTemplate,
  getCategoryLists,
  createName,
  addCustomDays,
  removeRecurringDate,
  getProductionDaysApi,
  updateBatchSplitApi,
} from "../api/TemplatesApi";
import {
  errorMessage,
  infoMessage,
  navigateBasedOnUserType,
  successMessage,
} from "../../../utils/helper";
import { useImmer } from "use-immer";
import { useNavigate } from "react-router-dom";

const TemplateListKeys = {
  all: ["templates"],
  lists: () => [...TemplateListKeys.all, "templatelist"],
  templatelist: (filters) => [...TemplateListKeys.lists(), filters],
  detail: (id, isCategoryBasedOtherRecipes) => [
    ...TemplateListKeys.templatelist(),
    id,
    isCategoryBasedOtherRecipes,
  ],
};

const productiondaysKeys = {
  all: ["productiondays"],
  list: () => [...productiondaysKeys.all, "productiondayslist"],
  productiondayslist: (filters) => [...productiondaysKeys.list(), { filters }],
  detail: (id) => [...productiondaysKeys.productiondayslist(), id],
};

const dropdownKeys = {
  all: ["days"],
  list: () => [...dropdownKeys.all, "dropdownlist"],
  dropdownlist: (filters) => [...dropdownKeys.list(), { filters }],
  detail: (id) => [...dropdownKeys.dropdownlist(), id],
};
const RecipeListKeys = {
  all: ["recipe"],
  lists: () => [...RecipeListKeys.all, "recipelist"],
  recipelist: (filters) => [...TemplateListKeys.lists(), filters],
  detail: (id) => [...RecipeListKeys.recipelist(), id],
};
const RecipeListsKeys = {
  all: ["recipe"],
  lists: () => [...RecipeListsKeys.all, "recipeslist"],
  recipeslist: (filters) => [...RecipeListsKeys.lists(), filters],
  detail: (id) => [...RecipeListsKeys.recipeslist(), id],
};

export const useTemplateQueries = ({
  debouncedText,
  sorting,
  pagination,
  status = -1,
  schedule = -1,
  resetDeleteInfo,
  details_id,
  dropdownlist,
  filterApplied,
  tempId,
  updateId,
  list,
  setTemplates,
  templates,
  isStoreAdmin,
  selectedDate,
  pc_temp_id,
  selectedIndex,
  isCategoryBasedOtherRecipes = 0,
}) => {
  const [state, setState] = useImmer({
    templateDetails: {},
    isCreate: false,
    isDuplicate: false,
    errorMessage: "",
    isErrorMessage: false,
  });
  const queryClient = useQueryClient();
  const storeId = filterApplied?.map((item) => item?.id);
  let params = {};
  const navigate = useNavigate();
  if (debouncedText) {
    params = { ...params, search: debouncedText };
  }
  if (storeId) {
    params = { ...params, stores: storeId };
  }
  if (status !== "") {
    params = { ...params, status: status };
  }
  if (schedule !== "") {
    params = { ...params, scheduled: schedule };
  }
  if (sorting?.length > 0) {
    const sortByParams = sorting?.reduce(
      (obj, item) =>
        Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
      {},
    );
    params = {
      ...params,
      sort: sortByParams,
    };
  }

  const query = {
    params,
    paramsSerializer: (params) => qs.stringify(params, { encode: true }),
  };

  //list query
  const listQuery = useQuery({
    queryKey: TemplateListKeys.templatelist({
      query,
      pagination,
    }),
    queryFn: () =>
      getTemplatesList({
        query,
        pagination,
      }),
    placeholderData: keepPreviousData,
  });

  //dropdownList
  const dropdownListQuery = useQuery({
    queryKey: dropdownKeys.dropdownlist(),
    queryFn: () => getDropDownLists(),
    placeholderData: keepPreviousData,
    enabled: dropdownlist,
  });

  //delete template
  const deleteTemplate = useMutation({
    mutationFn: ({ data, id }) => removeTemplate(id, data),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (res, variables) => {
      setTemplates((draft) => {
        draft.tempId = null;
        draft.pc_temp_id = null;
      });
      if (res.success) {
        if (variables.isList) {
          queryClient.invalidateQueries({
            queryKey: TemplateListKeys.lists(),
          });
        }
        successMessage(res.message);
        resetDeleteInfo();
        navigateBasedOnUserType("/templates", isStoreAdmin, navigate);
      } else if (res.success === false) {
        errorMessage(res.message);
      }
    },
  });

  let templateId = tempId ? tempId : details_id;
  if (templateId) templateId = Number(templateId);

  //template details
  const templateDetails = useQuery({
    queryFn: () => getTemplatesDetails(templateId, isCategoryBasedOtherRecipes),
    queryKey: TemplateListKeys.detail(templateId, isCategoryBasedOtherRecipes),
    // enabled: details_id > 0 || tempId > 0 || templates.scheduleOpen,
    enabled: templateId > 0,
  });

  const categorylistQuery = useQuery({
    queryKey: RecipeListKeys.recipelist(),
    queryFn: () => getCategoryLists(),
    placeholderData: keepPreviousData,
    enabled: list,
  });
  const recipelistQuery = useQuery({
    queryKey: RecipeListsKeys.recipeslist(),
    queryFn: () => getRecipeLists("finishedproduct"),
    enabled: templates.isOpen || templates.isOpenDialog,
  });

  const generateBatch = useMutation({
    mutationFn: ({ variable, data, id }) => createTemplate(data, id),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (data, variables) => {
      setState((draft) => {
        draft.templateDetails = data;
        draft.isCreate = data?.success ? true : false;
      });
      setTemplates((draft) => {
        draft.batchDetails = data?.data?.batches;
        draft.batch_template_id = data?.data?.template_id;
        draft.isShowErrorMessage = variables.type === "proceed" ? false : true;
        draft.isShowErrorMessage = false;
      });
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      data?.success
        ? successMessage(data?.message)
        : setTemplates((draft) => {
            draft.isShowErrorMessage = true;
            draft.errorMessageData = data;
          });
      return variables.handleClose();
    },
  });

  const reGenerateBatch = useMutation({
    mutationFn: ({ _, data }) => updateTemplate(updateId, data),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.templatelist(),
      });
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.detail(tempId),
      });

      setState((draft) => {
        draft.templateDetails = data;
        draft.isCreate = data?.success ? true : false;
      });
      setTemplates((draft) => {
        draft.batchDetails = data?.data?.batches;
        draft.isCreateDialog = data?.success ? true : false;
        draft.isShowErrorMessage = false;
      });
      data?.success
        ? successMessage(data?.message)
        : setTemplates((draft) => {
            draft.isShowErrorMessage = true;
            draft.errorMessageData = data;
          });
      variables.type === "update" && window.location.reload();
    },
  });
  const copyBatch = useMutation({
    mutationFn: ({ variable, data }) => copycreateTemplate(tempId, data),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (data, variables) => {
      setState((draft) => {
        draft.templateDetails = data;
        draft.isCreate = true;
        draft.isDuplicate = true;
      });

      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      successMessage("Template copy created successfully!!");
      return variables.handleClose();
    },
  });
  //template production days details
  const templateProductionDaysDetails = useQuery({
    queryKey: productiondaysKeys.detail(details_id),
    queryFn: () => getProductionDaysList(details_id),
    enabled: details_id > 0 || templates?.scheduleOpen,
  });

  //template scedule creation
  const createScedule = useMutation({
    mutationFn: ({ _, data }) => saveProductionDays(data),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      successMessage("Production days created successfully");
      variables.handleScheduleOpen();
      setTemplates((draft) => {
        draft.isLoading = false;
      });
      queryClient.invalidateQueries({
        queryKey: productiondaysKeys.detail(details_id),
      });
    },
  });

  // edit scheduler
  const updateSceduler = useMutation({
    mutationFn: editSceduler,
    onError: (error) => {
      errorMessage(error?.response?.data?.message);
    },
    onSuccess: (res, variables) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: productiondaysKeys.list(),
        });
        if (res?.data?.chartRunStatus === 1) {
          infoMessage(
            "The templates were successfully scheduled and will be added to the production chart in 10 minutes.",
          );
        } else {
          successMessage(res?.message);
        }
        if (isStoreAdmin) {
          setTemplates((draft) => {
            draft.isOpenDialog = false;
            draft.isOpen = false;
          });
          navigate(
            `/store-admin/templates/details/${variables?.data?.template_id}`,
          );
        }
        variables.handleClickUpdateOpen();
        details_id && variables.handleScheduleOpen();
        setTemplates((draft) => {
          draft.isLoading = false;
          draft.isCreateStoreAssign = isStoreAdmin ? false : true;
          draft.isCreateDateAssign = false;
          draft.isCreate = false;
        });
        queryClient.invalidateQueries({
          queryKey: productiondaysKeys.detail(details_id),
        });
      } else {
        errorMessage(res.message);
      }
    },
  });

  //update status
  const updateTemplateStatus = useMutation({
    mutationFn: updateStatus,
    onError: (error) => {
      setState((draft) => {
        draft.errorMessage = error?.response?.data?.errors;
      });
      errorMessage(error?.response?.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      successMessage(_.message);
      setTemplates((draft) => {
        draft.isLoading = false;
        draft.isDeleteScheduledDatesDialogOpen = false;
      });
      variables.handleClickOpen();
    },
  });

  //edit name

  const createTemplateName = useMutation({
    mutationFn: createName,
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      successMessage(data?.message);
      setTemplates((draft) => {
        draft.isCreateDateAssign = true;
      });
      details_id &&
        navigateBasedOnUserType(
          `/templates/details/${state?.templateDetails?.data?.template_id}?settings`,
          isStoreAdmin,
          navigate,
        );
    },
  });

  const updateTemplateName = useMutation({
    mutationFn: updateName,
    onError: (error) => {
      errorMessage(error?.response?.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });
      successMessage(_.message);
      variables.handleNameOpen();
    },
  });
  const handleAddCustomDays = useMutation({
    mutationFn: ({ data }) => addCustomDays(data),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.lists(),
      });

      setTemplates((draft) => {
        draft.isDateSaveButton = true;
        draft.data.custom_dates_view = templates.data.custom_dates_view.filter(
          (value) => {
            return value !== selectedDate;
          },
        );
      });
      if (data.chartRunStatus === 1) {
        infoMessage(
          "The templates were successfully scheduled and will be added to the production chart in 10 minutes.",
        );
      } else {
        successMessage(data?.message);
      }
      variables.onDeleteTagSelect(false);

      // **Refetch production days details after mutation success**
      templateProductionDaysDetails.refetch();
    },
  });

  //template recurring date delete
  const deleteRecurringDate = useMutation({
    mutationFn: removeRecurringDate,
    onError: (error) => {
      if (error?.response?.data?.message) {
        errorMessage(error.response.data.message);
      } else {
        errorMessage("An unknown error occurred");
      }
    },

    onSuccess: (res, variables) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: TemplateListKeys.templatelist(),
        });
        successMessage(res.message);
        setTemplates((draft) => {
          draft.isRecurringDateDelete = 1;
        });
        window.location.reload();

        if (typeof variables?.handleRemoveRecurringDate === "function") {
          variables.handleRemoveRecurringDate(false);
        }
      } else {
        errorMessage(
          res?.message ||
            "An error occurred while removing the recurring date.",
        );
      }
    },
  });

  //template production days list
  const templateProductionDaysList = useQuery({
    queryFn: () => getProductionDaysApi(pc_temp_id),
    queryKey: ["production-days", pc_temp_id],
    enabled: pc_temp_id ? true : false,
  });

  //batch split api integrations
  const updateBatchSplit = useMutation({
    mutationFn: ({ id, payload }) => updateBatchSplitApi(id, payload),
    onError: (error, variables) => {
      const errorData = error?.response?.data?.data;
      // Only show error if success is false and errorData is an array
      if (Array.isArray(errorData)) {
        setTemplates((draft) => {
          // Clear all previous errors first
          draft?.batchDetails?.[selectedIndex]?.products?.forEach((product) => {
            product.batches?.forEach((batch) => {
              batch.hasError = false;
            });
          });
          // Set error only for batches in errorData
          errorData.forEach((err) => {
            const product = draft?.batchDetails?.[
              selectedIndex
            ]?.products?.find((p) => p.product_id === err.product_id);
            if (
              product &&
              Array.isArray(product.batches) &&
              typeof err.batch === "number"
            ) {
              // API batch is 1-based, JS array is 0-based
              const batchIdx = err.batch - 1;
              if (product.batches[batchIdx]) {
                product.batches[batchIdx].hasError = true;
              }
            }
          });
        });
      }
      variables?.handleDeleteBatchDialog(false);
    },

    onSuccess: (data, variables) => {
      // If success, clear all errors
      if (data.success) {
        setTemplates((draft) => {
          draft?.batchDetails?.[selectedIndex]?.products?.forEach((product) => {
            product.batches?.forEach((batch) => {
              batch.hasError = false;
            });
          });
          draft.batchDetails = data?.data?.batches;
          const product =
            draft?.batchDetails?.[selectedIndex]?.products?.[
              templates.productIndex
            ];
          if (product?.batches?.[templates.batchIndex]) {
            product.batches[templates.batchIndex].hasError = data.success
              ? false
              : true;
          }
        });
      }
      queryClient.invalidateQueries({
        queryKey: TemplateListKeys.templatelist(),
      });
      variables.handleDeleteBatchDialog(false);
    },
  });

  return {
    listQuery,
    deleteTemplate,
    templateDetails,
    templateProductionDaysDetails,
    createScedule,
    dropdownListQuery,
    updateSceduler,
    updateTemplateStatus,
    categorylistQuery,
    recipelistQuery,
    generateBatch,
    state,
    reGenerateBatch,
    copyBatch,
    updateTemplateName,
    setState,
    createTemplateName,
    handleAddCustomDays,
    deleteRecurringDate,
    templateProductionDaysList,
    updateBatchSplit,
  };
};
